import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Shield, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'notaryos_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'notaryos_cookie_preferences';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    performance: false,
    preferences: false,
    thirdParty: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const saveConsent = (acceptAll = false, selectedPreferences = preferences) => {
    const finalPreferences = acceptAll 
      ? { essential: true, performance: true, preferences: true, thirdParty: true }
      : selectedPreferences;
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      timestamp: new Date().toISOString(),
      acceptedAll: acceptAll,
      version: '1.0'
    }));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(finalPreferences));
    setShowBanner(false);
    
    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: finalPreferences 
    }));
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleAcceptSelected = () => {
    saveConsent(false);
  };

  const handleRejectOptional = () => {
    const essentialOnly = {
      essential: true,
      performance: false,
      preferences: false,
      thirdParty: false
    };
    setPreferences(essentialOnly);
    saveConsent(false, essentialOnly);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[150] p-4 pointer-events-none"
          data-testid="cookie-consent-banner"
        >
          <div className="max-h-[calc(100dvh-2rem)] max-w-4xl mx-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-y-auto pointer-events-auto">
            {/* Main Banner */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Cookie className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Configuración de Cookies</h3>
                    <p className="text-sm text-slate-400">Ley 19.496 - Protección al Consumidor</p>
                  </div>
                </div>
                <button
                  data-testid="cookie-banner-close"
                  onClick={() => setShowBanner(false)}
                  className="text-slate-400 hover:text-white p-1"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-slate-300 text-sm mb-4">
                Utilizamos cookies para mejorar su experiencia en NotaryOS. Las cookies esenciales son 
                necesarias para el funcionamiento del sitio. Puede aceptar todas las cookies o personalizar 
                sus preferencias. 
                <Link data-testid="cookie-policy-link" to="/legal/cookies" className="text-blue-400 hover:underline ml-1">
                  Más información
                </Link>
              </p>

              {/* Toggle Details Button */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center space-x-2 text-sm text-slate-400 hover:text-white mb-4"
                data-testid="cookie-settings-toggle"
              >
                <Settings className="w-4 h-4" />
                <span>Personalizar preferencias</span>
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {/* Detailed Preferences */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-3 mb-4 overflow-hidden"
                  >
                    {/* Essential Cookies */}
                    <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <Shield className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Cookies Esenciales</p>
                          <p className="text-xs text-slate-400">Necesarias para el funcionamiento básico</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-emerald-400">Siempre activas</span>
                        <div className="w-10 h-6 bg-emerald-500 rounded-full flex items-center justify-end px-1">
                          <div className="w-4 h-4 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>

                    {/* Performance Cookies */}
                    <div 
                      className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800"
                      onClick={() => togglePreference('performance')}
                      data-testid="cookie-toggle-performance"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">Cookies de Rendimiento</p>
                        <p className="text-xs text-slate-400">Análisis de uso y métricas</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.performance ? 'bg-blue-500 justify-end' : 'bg-slate-600 justify-start'
                      }`}>
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>

                    {/* Preferences Cookies */}
                    <div 
                      className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800"
                      onClick={() => togglePreference('preferences')}
                      data-testid="cookie-toggle-preferences"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">Cookies de Preferencias</p>
                        <p className="text-xs text-slate-400">Recordar sus elecciones y personalización</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.preferences ? 'bg-blue-500 justify-end' : 'bg-slate-600 justify-start'
                      }`}>
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>

                    {/* Third Party Cookies */}
                    <div 
                      className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800"
                      onClick={() => togglePreference('thirdParty')}
                      data-testid="cookie-toggle-thirdparty"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">Cookies de Terceros</p>
                        <p className="text-xs text-slate-400">Servicios externos (Stripe, etc.)</p>
                      </div>
                      <div className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.thirdParty ? 'bg-blue-500 justify-end' : 'bg-slate-600 justify-start'
                      }`}>
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center space-x-2"
                  data-testid="cookie-accept-all"
                >
                  <Check className="w-4 h-4" />
                  <span>Aceptar Todas</span>
                </button>
                
                {showDetails ? (
                  <button
                    onClick={handleAcceptSelected}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
                    data-testid="cookie-accept-selected"
                  >
                    Guardar Preferencias
                  </button>
                ) : (
                  <button
                    onClick={handleRejectOptional}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
                    data-testid="cookie-reject-optional"
                  >
                    Solo Esenciales
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Hook to check cookie preferences
export const useCookiePreferences = () => {
  const [preferences, setPreferences] = useState({
    essential: true,
    performance: false,
    preferences: false,
    thirdParty: false
  });

  useEffect(() => {
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }

    const handleUpdate = (event) => {
      setPreferences(event.detail);
    };

    window.addEventListener('cookieConsentUpdated', handleUpdate);
    return () => window.removeEventListener('cookieConsentUpdated', handleUpdate);
  }, []);

  return preferences;
};

// Function to open cookie settings modal
export const openCookieSettings = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
};

export default CookieConsent;
